import { supabase } from "@/integrations/supabase/client";

let mapboxToken: string | null = null;

export async function getMapboxToken(): Promise<string> {
  if (mapboxToken) return mapboxToken;

  try {
    const { data, error } = await supabase.functions.invoke("get-mapbox-token");

    if (error) throw error;

    mapboxToken = data.token as string;
    return mapboxToken;
  } catch (error) {
    console.error(error);
    throw new Error("Mapbox token not configured");
  }
}

export async function geocodeAddress(address: string): Promise<[number, number]> {
  const token = await getMapboxToken();

  const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?access_token=${token}`;

  const response = await fetch(url);
  const data = await response.json();

  if (data.features && data.features.length > 0) {
    const [lng, lat] = data.features[0].center;
    return [lng, lat];
  }

  throw new Error("Address not found");
}

export function calculateDistance(point1: [number, number], point2: [number, number]): number {
  // Haversine formula for distance in meters
  const R = 6371e3; // Earth radius in meters
  const φ1 = (point1[1] * Math.PI) / 180;
  const φ2 = (point2[1] * Math.PI) / 180;
  const Δφ = ((point2[1] - point1[1]) * Math.PI) / 180;
  const Δλ = ((point2[0] - point1[0]) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}

export function formatDistance(meters: number): string {
  if (meters < 1000) {
    return `${Math.round(meters)} m`;
  }
  return `${(meters / 1000).toFixed(1)} km`;
}

export function estimateWalkingTime(meters: number): string {
  const walkingSpeedMps = 1.4; // 1.4 m/s average walking speed
  const minutes = Math.ceil(meters / walkingSpeedMps / 60);

  if (minutes < 1) return "less than 1 min";
  if (minutes === 1) return "1 min";
  return `${minutes} min`;
}
